import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CLUSTERS } from "@/lib/constants";
import { Check, ChevronLeft, ChevronRight, HeartHandshake, Building2, Church, Trophy, Users2, Loader2 } from "lucide-react";

const CLUSTER_ICONS = {
  "ODV / APS": HeartHandshake, "Cooperativa sociale": Building2,
  "Parrocchia / Ente religioso": Church, "ASD / SSD sportiva": Trophy, "Altro ETS": Users2,
};

export default function NewRequest() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [cluster, setCluster] = useState("");
  const [form, setForm] = useState({ ragione_sociale: "", referente: "", email: "", telefono: "",
    codice_fiscale: "", comune: "", provincia: "", note: "" });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const finish = async () => {
    setSaving(true);
    try {
      const { data: ana } = await api.post("/anagrafiche", { ...form, cluster, tipo_ente: cluster });
      const { data: req } = await api.post("/requests", {
        ragione_sociale: form.ragione_sociale, cluster, email: form.email,
        telefono: form.telefono, referente: form.referente, note: form.note,
      });
      toast.success("Richiesta creata. Procedi al preventivo.");
      navigate(`/quota?anagrafica=${ana.id}&request=${req.id}`);
    } catch (e) {
      toast.error(apiError(e));
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-semibold text-forest">Nuova richiesta</h1>
        <p className="mt-1 text-muted-foreground">Due passi e sei pronto a quotare.</p>
      </div>

      <div className="flex items-center gap-2">
        {[1, 2].map((s) => (
          <div key={s} className="flex flex-1 items-center gap-2">
            <div className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
              step >= s ? "bg-forest text-white" : "bg-sage text-forest/50"}`}>
              {step > s ? <Check className="h-4 w-4" /> : s}
            </div>
            <div className={`h-1 flex-1 rounded-full ${step > s ? "bg-forest" : "bg-sage"}`} />
          </div>
        ))}
      </div>

      {step === 1 && (
        <motion.div initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }}>
          <Card className="rounded-2xl border-border/60 p-6">
            <h2 className="font-heading text-xl font-semibold text-forest">Che tipo di ente è?</h2>
            <p className="mt-1 text-sm text-muted-foreground">Scegli il cluster per adattare la copertura.</p>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {CLUSTERS.map((c) => {
                const Icon = CLUSTER_ICONS[c] || Users2;
                return (
                  <button key={c} data-testid={`cluster-${c}`} onClick={() => setCluster(c)}
                    className={`flex items-center gap-3 rounded-xl border p-4 text-left transition-all ${
                      cluster === c ? "border-terracotta bg-terracotta/5 ring-1 ring-terracotta" : "border-border hover:border-forest/40 hover:bg-sage/40"}`}>
                    <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${cluster === c ? "bg-terracotta text-white" : "bg-sage text-forest"}`}>
                      <Icon className="h-5 w-5" strokeWidth={1.75} />
                    </div>
                    <span className="text-sm font-medium text-forest">{c}</span>
                  </button>
                );
              })}
            </div>
            <div className="mt-6 flex justify-end">
              <Button data-testid="wizard-next" disabled={!cluster} onClick={() => setStep(2)}
                className="gap-1 rounded-xl bg-terracotta hover:bg-terracotta-hover">
                Continua <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </Card>
        </motion.div>
      )}

      {step === 2 && (
        <motion.div initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }}>
          <Card className="rounded-2xl border-border/60 p-6">
            <h2 className="font-heading text-xl font-semibold text-forest">Dati dell'ente</h2>
            <p className="mt-1 text-sm text-muted-foreground">{cluster}</p>
            <div className="mt-5 space-y-4">
              <div className="space-y-1.5">
                <Label>Nome dell'ente *</Label>
                <Input data-testid="req-ragione-sociale" className="h-12" value={form.ragione_sociale} onChange={set("ragione_sociale")} />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5"><Label>Referente</Label><Input className="h-12" value={form.referente} onChange={set("referente")} /></div>
                <div className="space-y-1.5"><Label>Codice Fiscale</Label><Input className="h-12" value={form.codice_fiscale} onChange={set("codice_fiscale")} /></div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5"><Label>Email</Label><Input className="h-12" value={form.email} onChange={set("email")} /></div>
                <div className="space-y-1.5"><Label>Telefono</Label><Input className="h-12" value={form.telefono} onChange={set("telefono")} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Comune</Label><Input className="h-12" value={form.comune} onChange={set("comune")} /></div>
                <div className="space-y-1.5"><Label>Provincia</Label><Input className="h-12" value={form.provincia} onChange={set("provincia")} /></div>
              </div>
              <div className="space-y-1.5"><Label>Note</Label><Textarea value={form.note} onChange={set("note")} /></div>
            </div>
            <div className="mt-6 flex justify-between">
              <Button variant="ghost" onClick={() => setStep(1)} className="gap-1"><ChevronLeft className="h-4 w-4" /> Indietro</Button>
              <Button data-testid="wizard-finish" disabled={!form.ragione_sociale || saving} onClick={finish}
                className="gap-1 rounded-xl bg-terracotta hover:bg-terracotta-hover">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Vai al preventivo <ChevronRight className="h-4 w-4" /></>}
              </Button>
            </div>
          </Card>
        </motion.div>
      )}
    </div>
  );
}
